import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Info,
  X
} from 'lucide-react';
import { Notification as NotificationType, NotificationType as NotifType } from '../types';

interface NotificationProps {
  notifications: NotificationType[];
  onDismiss: (id: string) => void;
}

const iconMap: Record<NotifType, React.ElementType> = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info
};

const colorMap: Record<NotifType, {
  bg: string;
  border: string;
  icon: string;
  title: string;
}> = {
  success: {
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
    icon: 'text-green-400',
    title: 'text-green-300'
  },
  error: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    icon: 'text-red-400',
    title: 'text-red-300'
  },
  warning: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    icon: 'text-amber-400',
    title: 'text-amber-300'
  },
  info: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    icon: 'text-blue-400',
    title: 'text-blue-300'
  }
};

export function NotificationContainer({ notifications, onDismiss }: NotificationProps) {
  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-3 max-w-md w-full pointer-events-none">
      <AnimatePresence mode="sync">
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onDismiss={onDismiss}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

interface NotificationItemProps {
  notification: NotificationType;
  onDismiss: (id: string) => void;
}

function NotificationItem({ notification, onDismiss }: NotificationItemProps) {
  const Icon = iconMap[notification.type];
  const colors = colorMap[notification.type];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 100, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.9 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className={`
        pointer-events-auto
        flex items-start gap-3 p-4
        ${colors.bg} ${colors.border}
        border rounded-xl
        backdrop-blur-lg
        shadow-lg shadow-black/20
      `}
    >
      {/* Icon */}
      <div className="flex-shrink-0 mt-0.5">
        <Icon className={`w-5 h-5 ${colors.icon}`} />
      </div>

      {/* Content */}
      <div className="flex-grow min-w-0">
        <h4 className={`font-semibold text-sm ${colors.title}`}>
          {notification.title}
        </h4>
        <p className="text-sm text-dark-300 mt-0.5">
          {notification.message}
        </p>
      </div>

      {/* Close button */}
      <button
        onClick={() => onDismiss(notification.id)}
        className="flex-shrink-0 p-1 text-dark-400 hover:text-dark-200 transition-colors rounded"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Progress bar for auto-dismiss */}
      {notification.duration && (
        <motion.div
          className="absolute bottom-0 left-0 h-0.5 bg-current rounded-b-xl"
          initial={{ width: '100%' }}
          animate={{ width: '0%' }}
          transition={{ duration: notification.duration / 1000, ease: 'linear' }}
          style={{ color: colors.icon.replace('text-', '') }}
        />
      )}
    </motion.div>
  );
}

export default NotificationContainer;
